import React from 'react';
import { X, Check, Copy, Edit2, Users, Eye, EyeOff } from 'lucide-react';
import DriverProfileTabs from '../shared/DriverProfileTabs';

const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'NT', 'ACT'];

/* ===================================================================
   Invite Driver Modal
   =================================================================== */
export const InviteDriverModal = ({
  inviteForm, setInviteForm,
  inviteResult, loading,
  onSubmit, onCopy, onClose,
}) => (
  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
    <div className="bg-slate-800 rounded-2xl p-5 sm:p-6 max-w-lg w-full border border-slate-700 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Invite New Driver</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>

      {!inviteResult ? (
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">First Name *</label>
              <input type="text" value={inviteForm.first_name}
                onChange={e => setInviteForm({ ...inviteForm, first_name: e.target.value })}
                className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                required data-testid="invite-first-name" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Last Name *</label>
              <input type="text" value={inviteForm.last_name}
                onChange={e => setInviteForm({ ...inviteForm, last_name: e.target.value })}
                className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                required data-testid="invite-last-name" />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-400 mb-1 block">Email *</label>
            <input type="email" value={inviteForm.email}
              onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })}
              className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
              required data-testid="invite-email" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">License Number *</label>
              <input type="text" value={inviteForm.license_number}
                onChange={e => setInviteForm({ ...inviteForm, license_number: e.target.value })}
                className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                required data-testid="invite-license" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">License State</label>
              <select value={inviteForm.license_state}
                onChange={e => setInviteForm({ ...inviteForm, license_state: e.target.value })}
                className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                data-testid="invite-license-state">
                {AU_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">DL Card Number</label>
              <input type="text" value={inviteForm.license_card_number}
                onChange={e => setInviteForm({ ...inviteForm, license_card_number: e.target.value })}
                className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                placeholder="Card number" data-testid="invite-license-card" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Date of Birth</label>
              <input type="date" value={inviteForm.date_of_birth}
                onChange={e => setInviteForm({ ...inviteForm, date_of_birth: e.target.value })}
                className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm [color-scheme:dark]"
                data-testid="invite-dob" />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-400 mb-1 block">Phone (Optional)</label>
            <input type="tel" value={inviteForm.phone}
              onChange={e => setInviteForm({ ...inviteForm, phone: e.target.value })}
              className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
              data-testid="invite-phone" />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-slate-700 text-white rounded-lg text-sm">Cancel</button>
            <button type="submit" disabled={loading}
              className="flex-1 py-2.5 bg-emerald-500 text-white rounded-lg text-sm disabled:opacity-50"
              data-testid="submit-invite-btn">
              {loading ? 'Creating...' : 'Create & Get Link'}
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-emerald-500/20 rounded-xl">
            <Check className="w-5 h-5 text-emerald-400" />
            <p className="text-emerald-400 text-sm">Driver created successfully!</p>
          </div>

          <div className="space-y-3">
            <p className="text-white font-medium">{inviteResult.driver?.name}</p>
            <p className="text-slate-400 text-sm">License: {inviteResult.driver?.license}</p>
          </div>

          <div className="bg-slate-700/50 rounded-xl p-4 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-slate-400 text-xs">Invite Link (24hr expiry)</p>
              <button onClick={() => onCopy(inviteResult.invite_url, 'Invite link')}
                className="text-emerald-400 hover:text-emerald-300 text-xs flex items-center gap-1"
                data-testid="copy-invite-link">
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <p className="text-white text-xs font-mono break-all">{inviteResult.invite_url}</p>
          </div>

          <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/30">
            <p className="text-amber-400 text-xs mb-2">Manual Login Credentials</p>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-slate-300 text-sm">Email:</span>
                <div className="flex items-center gap-2">
                  <span className="text-white text-sm font-mono">{inviteResult.credentials?.email}</span>
                  <button onClick={() => onCopy(inviteResult.credentials?.email, 'Email')} className="text-slate-400 hover:text-white"><Copy className="w-3 h-3" /></button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-300 text-sm">Password:</span>
                <div className="flex items-center gap-2">
                  <span className="text-white text-sm font-mono">{inviteResult.credentials?.password}</span>
                  <button onClick={() => onCopy(inviteResult.credentials?.password, 'Password')} className="text-slate-400 hover:text-white"><Copy className="w-3 h-3" /></button>
                </div>
              </div>
            </div>
          </div>

          <button onClick={onClose} className="w-full py-2.5 bg-slate-700 text-white rounded-lg text-sm">Done</button>
        </div>
      )}
    </div>
  </div>
);

/* ===================================================================
   Driver Detail Modal (with Profile Tabs)
   =================================================================== */
export const DriverDetailModal = ({
  driver, driverState, onEdit, onClose,
}) => (
  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
    <div className="bg-slate-800 rounded-2xl p-5 sm:p-6 max-w-lg w-full border border-slate-700 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Driver Details</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>

      <div className="flex items-center gap-4 mb-6">
        <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center">
          <Users className="w-8 h-8 text-slate-400" />
        </div>
        <div>
          <p className="text-xl font-semibold text-white">{driver.users?.first_name} {driver.users?.last_name}</p>
          <p className="text-slate-400 text-sm">{driver.users?.email}</p>
          <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs ${
            driver.status === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600 text-slate-400'
          }`}>{driver.status}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-slate-700/30 rounded-xl p-3">
          <p className="text-slate-400 text-xs mb-1">License</p>
          <p className="text-white font-medium">{driver.license_number || '-'}</p>
          <p className="text-slate-500 text-xs">{driver.license_state}</p>
        </div>
        <div className="bg-slate-700/30 rounded-xl p-3">
          <p className="text-slate-400 text-xs mb-1">Timezone</p>
          <p className="text-white font-medium text-sm">{driver.base_timezone || '-'}</p>
        </div>
        <div className="bg-slate-700/30 rounded-xl p-3 col-span-2">
          <p className="text-slate-400 text-xs mb-1">Base Address</p>
          <p className="text-white">{driver.base_address || 'Not set'}</p>
        </div>
        {driver.users?.phone && (
          <div className="bg-slate-700/30 rounded-xl p-3 col-span-2">
            <p className="text-slate-400 text-xs mb-1">Phone</p>
            <p className="text-white">{driver.users.phone}</p>
          </div>
        )}
      </div>

      <div className="border-t border-slate-700 pt-4 mb-4">
        <DriverProfileTabs
          driverId={driver.driver_id}
          driverName={`${driver.users?.first_name} ${driver.users?.last_name}`}
          complianceState={driverState}
          canVerify={true}
        />
      </div>

      <div className="flex gap-3">
        <button onClick={onEdit}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-slate-700 text-white rounded-lg text-sm hover:bg-slate-600"
          data-testid="edit-driver-btn">
          <Edit2 className="w-4 h-4" /> Edit
        </button>
        <button onClick={onClose}
          className="flex-1 py-2.5 bg-emerald-500 text-white rounded-lg text-sm">Close</button>
      </div>
    </div>
  </div>
);

/* ===================================================================
   Edit Driver Modal
   =================================================================== */
export const EditDriverModal = ({
  driver, editForm, setEditForm,
  loading, onSave, onClose,
}) => (
  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
    <div className="bg-slate-800 rounded-2xl p-5 sm:p-6 max-w-lg w-full border border-slate-700 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Edit Driver</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>

      <div className="space-y-4">
        <div className="p-3 bg-slate-700/30 rounded-lg">
          <p className="text-white font-medium">{driver.users?.first_name} {driver.users?.last_name}</p>
          <p className="text-slate-400 text-xs">{driver.users?.email}</p>
        </div>

        <div className="border-t border-slate-700 pt-4">
          <h4 className="text-sm font-medium text-slate-300 mb-3">Driver License Details</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">DL State</label>
              <select value={editForm.license_state}
                onChange={e => setEditForm({ ...editForm, license_state: e.target.value })}
                className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                data-testid="edit-license-state">
                {AU_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">DL Number</label>
              <input type="text" value={editForm.license_number}
                onChange={e => setEditForm({ ...editForm, license_number: e.target.value })}
                className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                placeholder="e.g., 12345678" data-testid="edit-license-number" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">DL Card Number</label>
              <input type="text" value={editForm.license_card_number}
                onChange={e => setEditForm({ ...editForm, license_card_number: e.target.value })}
                className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
                placeholder="e.g., ABC123456" data-testid="edit-license-card" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Date of Birth</label>
              <input type="date" value={editForm.date_of_birth}
                onChange={e => setEditForm({ ...editForm, date_of_birth: e.target.value })}
                className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm [color-scheme:dark]"
                data-testid="edit-dob" />
            </div>
          </div>
        </div>

        <div className="border-t border-slate-700 pt-4">
          <label className="text-xs text-slate-400 mb-1 block">Base Address</label>
          <input type="text" value={editForm.base_address}
            onChange={e => setEditForm({ ...editForm, base_address: e.target.value })}
            className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
            placeholder="e.g., 10 Railway Terrace, North Sydney" data-testid="edit-base-address" />
          <p className="text-xs text-slate-500 mt-1">Timezone will be auto-detected from address</p>
        </div>

        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose}
            className="flex-1 py-2.5 bg-slate-700 text-white rounded-lg text-sm">Cancel</button>
          <button onClick={onSave} disabled={loading}
            className="flex-1 py-2.5 bg-emerald-500 text-white rounded-lg text-sm disabled:opacity-50"
            data-testid="save-driver-btn">
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  </div>
);
